import { AddIcon, DeleteIcon } from "@chakra-ui/icons";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Center,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Switch,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  HStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";

import AddAdminPanel from "./AddAdminPanel";
import { AdminService } from "src/rest/adminService";
import { AdminUser } from "src/types/commonTypes";
import { StatusCode } from "src/common/constants/constantEnums";
import { UseAppToast } from "src/common/useAppToast";
import { useSelector } from "react-redux";
import { RootState } from "src/store/store";
import { useRef } from "react";
import { Admin_Labels_Eng } from "src/common/constants/englishLabels";
import { Admin_Labels_Hindi } from "src/common/constants/hindiLabels";
import {
  API_STATUS_APPROVE,
  API_STATUS_REJECT
} from "src/common/constants/statusConstants";

function AdminInfo() {
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null);
  const {
    isOpen: isDrawerOpen,
    onOpen: onDrawerOpen,
    onClose: onDrawerClose,
  } = useDisclosure();
  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onDeleteDialogOpen,
    onClose: onDeleteDialogClose,
  } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const { successToast, errorToast } = UseAppToast();

  const globalLanguage = useSelector(
    (state: RootState) => state.globalInfo.globalLanguage
  );
   const isHindiLabels = globalLanguage === "HI";
  const fetchAdmins = async () => {
    setIsLoading(true);
    try {
      const response = await AdminService.getAdmins();
      if (response.status === StatusCode.SUCCESS_STATUS_CODE) {
        const raw = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];
        const list: AdminUser[] = (raw as any[]).map((val: any) => ({
          ...val,
          status: val?.approved === true ? "Approved" : "Rejected",
        }));
        setAdmins(list);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch admins";
      errorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleStatusToggle = async (admin: AdminUser) => {
    // Determine new UI status ("Approved"/"Rejected")
    const newStatus: AdminUser["status"] = admin.status === "Rejected" ? "Approved" : "Rejected";
    // Determine backend status value
    const backendStatus: typeof API_STATUS_APPROVE | typeof API_STATUS_REJECT = newStatus === "Approved" ? API_STATUS_APPROVE : API_STATUS_REJECT;
    try {
      const response = await AdminService.updateAdminStatus(
        admin.email,
        backendStatus // send correct backend value
      );
      if (response.status === StatusCode.SUCCESS_STATUS_CODE) {
        setAdmins((prevAdmins) =>
          prevAdmins.map((a) =>
            a.email === admin.email
              ? { ...a, status: newStatus, approved: newStatus === "Approved" }
              : a
          )
        );
        successToast(
          `Admin ${newStatus === "Approved" ? "unblocked" : "blocked"} successfully`
        );
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to update admin status";
      errorToast(errorMessage);
    }
  };

  const handleDeleteClick = (admin: AdminUser) => {
    setSelectedAdmin(admin);
    onDeleteDialogOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedAdmin) return;

    setIsDeleting(true);
    try {
      const response = await AdminService.deleteAdmin(selectedAdmin.email);
      if (response.status === StatusCode.SUCCESS_STATUS_CODE) {
        setAdmins((prevAdmins) =>
          prevAdmins.filter((a) => a.email !== selectedAdmin.email)
        );
        successToast("Admin deleted successfully");
        onDeleteDialogClose();
        setSelectedAdmin(null);
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete admin";
      errorToast(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Box width="100%">
      {/* Header Section */}
      <Flex justify="space-between" align="center" mb={8}>
        <Heading size="lg" color="#4A2C2A" fontWeight={700}>
          {isHindiLabels ? "व्यवस्थापक प्रबंधन" : "Admin Management"}
        </Heading>
        <Button
          leftIcon={<AddIcon />}
          bg="#4A2C2A"
          color="white"
          onClick={onDrawerOpen}
          _hover={{ bg: "#6B0000" }}
          _active={{ bg: "#5B0000" }}
          fontWeight={600}
        >
           {isHindiLabels ? Admin_Labels_Hindi.ADMIN_MANAGEMENT : Admin_Labels_Eng.ADMIN_MANAGEMENT}
        </Button>
      </Flex>

      {/* Loading State */}
      {isLoading ? (
        <Center py={20}>
          <Spinner size="lg" color="#8B0000" />
        </Center>
      ) : admins.length === 0 ? (
        <Center
          py={20}
          borderRadius="md"
          border="1px solid #E5E7EB"
          bg="#F8F7F5"
        >
          <Text fontSize="md" color="#5C5757">
            {isHindiLabels? 'कोई प्रशासक नहीं मिला। नया बनाने के लिए "प्रशासक जोड़ें" पर क्लिक करें।'
                          : 'No admins found. Click "Add Admin" to create one.'}
          </Text>
        </Center>
      ) : (
        <TableContainer borderRadius="md" border="1px solid #E5E7EB" bg="white" fontFamily="PP Neue Montreal">
          <Table variant="simple" size="md">
            <Thead bg="#F8F7F5" borderBottom="2px solid #E5E7EB">
              <Tr>
                <Th fontSize="sm" fontWeight={600} color="#2C292A">
                  {isHindiLabels? Admin_Labels_Hindi.EMAIL:Admin_Labels_Eng.EMAIL}
                </Th>
                <Th fontSize="sm" fontWeight={600} color="#2C292A">
                  {isHindiLabels? Admin_Labels_Hindi.ROLE:Admin_Labels_Eng.ROLE}
                </Th>
                <Th fontSize="sm" fontWeight={600} color="#2C292A">
                  {isHindiLabels? Admin_Labels_Hindi.STATUS:Admin_Labels_Eng.STATUS}
                </Th>
                <Th fontSize="sm" fontWeight={600} color="#2C292A">
                  {isHindiLabels? Admin_Labels_Hindi.BLOCKED:Admin_Labels_Eng.BLOCKED}
                </Th>
                <Th fontSize="sm" fontWeight={600} color="#2C292A">
                  {isHindiLabels? Admin_Labels_Hindi.ACTION:Admin_Labels_Eng.ACTION }
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {admins.map((admin) => (
                <Tr
                  key={admin.email}
                  borderBottom="1px solid #E5E7EB"
                  _hover={{ bg: "#F8F7F5" }}
                >
                  <Td fontSize="sm" color="#2C292A">
                    {admin.email}
                  </Td>
                  <Td fontSize="sm" color="#5C5757">
                    Admin
                  </Td>
                  <Td fontSize="sm">
                    <Box
                      as="span"
                      px={3}
                      py={1}
                      borderRadius="full"
                      fontSize="xs"
                      fontWeight={600}
                      bg={admin.status === "Approved" ? "#E6F3E6" : "#FFE6E6"}
                      color={admin.status === "Approved" ? "#2D5F2D" : "#8B0000"}
                    >
                      {admin.status === "Approved" ? "ACTIVE" : "BLOCKED"}
                    </Box>
                  </Td>
                  <Td>
                    <HStack>
                      <Switch
                        isChecked={admin.status === "Rejected"}
                        onChange={() => handleStatusToggle(admin)}
                        colorScheme="red"
                        size="md"
                        aria-label="Toggle blocked"
                      />
                      <Text fontSize="sm" color="#5C5757">
                        {admin.status === "Rejected" ? "Blocked" : "Active"}
                      </Text>
                    </HStack>
                  </Td>
                  <Td>
                    <IconButton
                      icon={<DeleteIcon />}
                      aria-label="Delete admin"
                      colorScheme="red"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(admin)}
                      _hover={{ bg: "#FFE6E6" }}
                    />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* Add Admin Drawer */}
      <AddAdminPanel
        isOpen={isDrawerOpen}
        onClose={onDrawerClose}
        onAdminAdded={fetchAdmins}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteDialogClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="md">
            <AlertDialogHeader fontSize="lg" fontWeight={600} color="#8B0000">
              Delete Admin
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete{" "}
              <strong>{selectedAdmin?.email}</strong>? This action cannot be
              undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteDialogClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDeleteConfirm}
                ml={3}
                isLoading={isDeleting}
                loadingText="Deleting..."
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

export default AdminInfo;
